import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SortField, SortOrder, ViewMode } from "@/components/candidates/CandidatesViewOptions";
import { useSettings } from "@/providers/SettingsProvider";

interface JobsFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  skillsOperator: "AND" | "OR";
  setSkillsOperator: (operator: "AND" | "OR") => void;
  statusFilter: string[];
  setStatusFilter: (statuses: string[]) => void;
  showClosed: boolean;
  setShowClosed: (show: boolean) => void;
  maxDaysAgo: number | null;
  setMaxDaysAgo: (days: number | null) => void;
  assignedToMe: boolean;
  setAssignedToMe: (assigned: boolean) => void;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: (show: boolean) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  clearAllFilters: () => void;
}

interface JobsFilterProviderProps {
  children: ReactNode;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const JobsFilterContext = createContext<JobsFilterContextType | undefined>(undefined);

export function JobsFilterProvider({
  children,
  viewMode: externalViewMode,
  setViewMode: externalSetViewMode
}: JobsFilterProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { getDaysFilterForEntity, setDaysFilterForEntity } = useSettings();

	// Initialize state from URL parameters
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [skillsFilter, setSkillsFilter] = useState<string[]>(() => {
		const skills = searchParams.get("skills");
		return skills ? skills.split(",") : [];
	});
	const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">(() => {
		const operator = searchParams.get("skillsOp");
		return operator === "OR" ? "OR" : "AND";
	});
	const [statusFilter, setStatusFilter] = useState<string[]>(() => {
		const status = searchParams.get("status");
		return status ? status.split(",").filter(s => s.trim() !== "") : [];
	});
	const [showClosed, setShowClosed] = useState<boolean>(() => searchParams.get("showClosed") === "true");
	const [maxDaysAgo, setMaxDaysAgo] = useState<number | null>(() => {
		const days = searchParams.get("maxDays");
		return days ? parseInt(days, 10) : getDaysFilterForEntity('jobs');
	});
	const [assignedToMe, setAssignedToMe] = useState<boolean>(() => searchParams.get("assignedToMe") === "true");
	const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => searchParams.get("favoritesOnly") === "true");
	const [sortField, setSortField] = useState<SortField>(() => {
		const field = searchParams.get("sortField");
		return (field as SortField) || "name";
	});
	const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
		const order = searchParams.get("sortOrder");
		return (order as SortOrder) || "asc";
	});
	const [internalViewMode, setInternalViewMode] = useState<ViewMode>("card");
	const [currentPage, setCurrentPage] = useState(() => {
		const page = searchParams.get("page");
		return page ? parseInt(page, 10) : 1;
	});

	// Use either external view mode state or internal
	const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
	const setViewMode = externalSetViewMode || setInternalViewMode;

	// Update URL parameters whenever filters change
	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		
		// Clear only filter-related params
		params.delete("search");
		params.delete("skills");
		params.delete("skillsOp");
		params.delete("status");
		params.delete("showClosed");
		params.delete("maxDays");
		params.delete("assignedToMe");
		params.delete("favoritesOnly");
		params.delete("sortField");
		params.delete("sortOrder");
		params.delete("page");
		
		if (searchTerm)
			params.set("search", searchTerm);
		if (skillsFilter.length > 0)
			params.set("skills", skillsFilter.join(","));
		if (skillsOperator !== "AND")
			params.set("skillsOp", skillsOperator);
		if (statusFilter.length > 0)
			params.set("status", statusFilter.join(","));
		if (showClosed)
			params.set("showClosed", "true");
		if (maxDaysAgo !== 60 && maxDaysAgo !== null)
			params.set("maxDays", maxDaysAgo.toString());
		if (assignedToMe)
			params.set("assignedToMe", "true");
		if (showFavoritesOnly)
			params.set("favoritesOnly", "true");
		if (sortField !== "name")
			params.set("sortField", sortField);
		if (sortOrder !== "asc")
			params.set("sortOrder", sortOrder);
		if (currentPage !== 1)
			params.set("page", currentPage.toString());
		
		setSearchParams(params, { replace: true });
	}, [searchTerm, skillsFilter, skillsOperator, statusFilter, showClosed, maxDaysAgo, assignedToMe, showFavoritesOnly, sortField, sortOrder, currentPage, searchParams]);

	const handleSetMaxDaysAgo = useCallback((days: number | null) => {
		setMaxDaysAgo(days);
		setDaysFilterForEntity('jobs', days);
	}, [setDaysFilterForEntity]);

	const clearAllFilters = () => {
		setSearchTerm("");
		setSkillsFilter([]);
		setSkillsOperator("AND");
		setStatusFilter([]);
		setShowClosed(false);
		setMaxDaysAgo(getDaysFilterForEntity('jobs')); // Reset to saved preference
		setAssignedToMe(false);
		setShowFavoritesOnly(false);
		setCurrentPage(1);
	};

  return (
    <JobsFilterContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        skillsFilter,
        setSkillsFilter,
        skillsOperator,
        setSkillsOperator,
        statusFilter,
        setStatusFilter,
        showClosed,
        setShowClosed,
        maxDaysAgo,
        setMaxDaysAgo: handleSetMaxDaysAgo,
        assignedToMe,
        setAssignedToMe,
        showFavoritesOnly,
        setShowFavoritesOnly,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        viewMode,
        setViewMode,
        currentPage,
        setCurrentPage,
        clearAllFilters,
      }}
    >
      {children}
    </JobsFilterContext.Provider>
  );
}

export const useJobsFilter = () => {
  const context = useContext(JobsFilterContext);
  if (context === undefined) {
    throw new Error("useJobsFilter must be used within a JobsFilterProvider");
  }
  return context;
}; 